import { SwitchTenantWrapper } from "./Style.ts";
import { ISwitchTenantProps } from "@/views/layout/header/components/switch-tenant/Model.ts";
import { useEffect, useId, useRef, useState } from "react";
import { TenantInfo } from "@/services/tenant-service/entities/TenantEntity.ts";
import { cloneDeep } from "lodash";
import { PositioningImperativeRef } from "@fluentui/react-components";
import { Checkmark20Regular, ChevronDown20Regular, Home20Regular, Home24Regular } from "@fluentui/react-icons";
import { ETheme, Popover, PopoverSurface, SearchBox, Spinner, Tooltip } from "@/DependenciesV9";
import { useSelector } from "react-redux";
import { RootState } from "@/redux";
import { UserLoginInfo } from "@/services/login-service/entities/UserLoginEntity.ts";
import { useTranslation } from "react-i18next";
import { onHandleGetTenantId } from "@/commons/RCAttribute.ts";

const SwitchTenant = (props: ISwitchTenantProps) => {
  const {t} = useTranslation();
  const [tenantList, setTenantList] = useState<TenantInfo[]>([]);
  const [optsFilter, setOptsFilter] = useState<TenantInfo[]>([]);
  const [tenantName, setTenantName] = useState<string>("Root Tenant");
  const [keyword, setKeyword] = useState<string>("");
  const [loading] = useState(false);
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLInputElement>(null);
  const headerId = useId();
  const positioningRef = useRef<PositioningImperativeRef>(null);
  const [, setTenantSwitch] = useState<TenantInfo | undefined>();
  const [isVisibleField, setVisibleField] = useState<boolean>(false);

  const userInfo: UserLoginInfo = useSelector(
    (state: RootState) => state.settingInfo?.userInfo
  );
  const theme: ETheme = useSelector(
    (state: RootState) => state.settingInfo?.theme
  );

  useEffect(() => {
    if(wrapperRef?.current && positioningRef.current) {
      positioningRef.current?.setTarget(wrapperRef.current);
    }
  }, [wrapperRef, positioningRef]);

  useEffect(() => {
    getTenantList();
  }, [])

  const getTenantList = async () => {
    if(props.onFetchTenantList) {
      const tenantListResp = await props.onFetchTenantList();
      setTenantList(tenantListResp);
      setOptsFilter(cloneDeep(tenantListResp));
    }
  }

  const openListTenant = () => {
    setOpen((s) => !s);
  };

  const onSearch = (_e: any, value?: string) => {
    console.log("onSearch", value);
    const data = cloneDeep(tenantList);
    setKeyword(value || "");
    setOptsFilter(
      data.filter((item: TenantInfo) => {
        return (
          item?.name?.toLowerCase().indexOf((value || "")?.toLowerCase()) !== -1
        );
      })
    );
  };

  const onHandleGetValue = (item: TenantInfo) => {
    if(item) {
      const tenantId = onHandleGetTenantId();
      if(item.guid !== tenantId) {
        setTenantName(item.name || "");
        setTenantSwitch(item);
        setVisibleField(!isVisibleField);
      }
    }
  };

  return (
    <SwitchTenantWrapper className="SwitchTenantWrapper">
      <div className="cursor-pointer" ref={wrapperRef}>
        {tenantName && (
          <div
            onClick={openListTenant}
            className={`d-flex custom-input header__sync`}
          >
            <div className="icon-wrapper">
              <Home24Regular className="cursor-pointer custom-icon"/>
            </div>
            <label className="custom-display-value custom-display-text-select">
              {tenantName}
            </label>
            <div className="icon-wrapper">
              <ChevronDown20Regular className="cursor-pointer icon-chevron-down mt-2"/>
            </div>
          </div>
        )}
      </div>
      <Popover
        onOpenChange={(_, data) => setOpen(data.open)}
        trapFocus
        open={open}
        positioning={{
          positioningRef,
          position: "below",
          align: "end",
        }}
        mountNode={undefined}
        theme={theme}
      >
        <PopoverSurface
          aria-labelledby={headerId}
          className="width_custom_popover"
          style={{
            maxHeight: "370px",
            overflow: "auto",
            padding: "4px",
            overflowY: "hidden",
            height: "370px"
          }}
        >
          <>
            <div
              style={{
                width: "270px",
                padding: "10px",
                borderBottom: "1px solid #C4C4C4",
              }}
            >
              <SearchBox
                placeholder={t("Search")}
                id="search.box"
                onChangeText={onSearch}
                value={keyword}
                style={{width: "100%"}}
                theme={theme}
              />
            </div>
            <>
              {loading ? (
                <div style={{width: "100%", height: "200px"}}>
                  <Spinner theme={theme}/>
                </div>
              ) : optsFilter.length ? (
                <div
                  style={{
                    maxHeight: "300px",
                    overflowY: "auto",
                  }}
                  className={`scrollbar-custom-${props.theme}`}
                >
                  {optsFilter.map((item: TenantInfo) => (
                    <div
                      style={{
                        marginBottom: "5px",
                        height: "36px",
                        cursor: "pointer",
                        paddingLeft: "10px",
                        paddingRight: "10px",
                        display: "flex",
                        alignItems: "center",
                      }}
                      key={item.guid}
                      onClick={() =>
                        !item.disabled ? onHandleGetValue(item) : undefined
                      }
                      className={
                        userInfo?.tenantInfo.name?.trim() === item.name?.trim()
                          ? props.theme === "dark"
                            ? `hover-item-dark active-item-dark`
                            : `hover-item active-item`
                          : props.theme === "dark"
                            ? "hover-item-dark"
                            : "hover-item"
                      }
                    >
                      <Home20Regular
                        className={`cursor-pointer custom-icon-small color-${props.theme}`}
                        style={{
                          paddingRight: "5px",
                          opacity: item.disabled ? 0.6 : 1,
                        }}
                      />
                      <Tooltip
                        content={item.name || ""}
                        relationship="label"
                        theme={theme}
                      >
                        <div
                          style={{
                            whiteSpace: "nowrap",
                            maxWidth: "200px",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            cursor: "pointer",
                            opacity: item.disabled ? 0.6 : 1,
                          }}
                          className={`color-${props.theme}`}
                        >
                          {t(item.name || "")}
                        </div>
                      </Tooltip>
                      {userInfo?.tenantInfo.name?.trim() ===
                        item.name?.trim() && (
                          <div
                            className={`color-${props.theme}`}
                            style={{paddingLeft: "10px"}}
                          >
                            <Checkmark20Regular/>
                          </div>
                        )}
                    </div>
                  ))}
                </div>
              ) : (
                <div
                  className="listEmptyWrapper"
                  style={{display: "flex", justifyContent: "center"}}
                >
                  <h5
                    className={`listEmpty__title color-${props.theme}`}
                    style={{fontSize: "14px"}}
                  >
                    {t("No data available")}.
                  </h5>
                  <span className="listEmpty__detail"></span>
                </div>
              )}
            </>
          </>
        </PopoverSurface>
      </Popover>
    </SwitchTenantWrapper>
  );
};

export default SwitchTenant;